import React from 'react';

import { TFunction } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import AsyncSelect from '@/components/AsyncSelect';
import { renderUserOption, renderUserTag } from '@/components/AsyncSelect/entity/user/renderers';
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
import AIPraiseMessage from '@/components/LyearnAI/components/Editor/components/AIPraiseMessage';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import { UserFragment } from '@/graphql/fragments/generated/User';
import {
  PRAISE_MESSAGE_TOOLBAR_ADD_EMOJI_KEY,
  PRAISE_MESSAGE_TOOLBAR_ADD_MENTION_KEY,
} from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage/consts';
import { asyncSelectClasses } from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseRecipients/styles';
import { renderInput } from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseRecipients/utils';
import { TextUseCaseEnum } from '@/types/schema';

import RelatedSkills from './components/RelatedSkills';
import { InitialFormValueType } from './types';

export const initialValues: InitialFormValueType = {
  requestedFromUserIds: [],
  requestMessage: '',
  skillIds: [],
  mindsetIds: [],
};

export const validateMeetingForm = (t: TFunction, values: InitialFormValueType): any => {
  const errors: any = {};

  if (!values.requestedFromUserIds.length) {
    errors.requestedFromUserIds = t('Feedback:RequestFeedback.RequestedFromUserIds.EmptyError');
  }

  if (!values.requestMessage) {
    errors.requestMessage = t('Feedback:RequestFeedback.RequestMessage.EmptyError');
  }

  return errors;
};

const QUERY_PARAMS = { excludeSelf: true };

export const getFields = ({ t }: { t: TFunction }): Fields<InitialFormValueType> => ({
  requestedFromUserIds: {
    name: 'requestedFromUserIds',
    type: FieldType.Custom,
    label: t('Feedback:RequestFeedback.RequestedFromUserIds.Title'),
    Component: ({ value, onChange, error, touched }) => (
      <AsyncSelect
        disableClearable
        multiple
        classes={asyncSelectClasses}
        error={touched && error}
        optionLabelRenderer={renderUserOption}
        placeholder={
          value.length ? '' : t('Feedback:RequestFeedback.RequestedFromUserIds.Placeholder')
        }
        popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
        queryParams={QUERY_PARAMS}
        renderInput={(params) => renderInput(value, params)}
        tagLabelRenderer={renderUserTag}
        useOptionsQuery={useUsers}
        value={value as UserFragment[] as TSFixMe}
        onChange={onChange}
      />
    ),
  },
  requestMessage: {
    name: 'requestMessage',
    type: FieldType.Custom,
    label: t('Feedback:RequestFeedback.RequestMessage.Title'),
    Component: ({
      value,
      touched,
      error,
      onChange,
      setFieldValue,
      setFieldTouched,
      setFieldError,
    }) => (
      <AIPraiseMessage
        editorOptions={[
          PRAISE_MESSAGE_TOOLBAR_ADD_EMOJI_KEY,
          PRAISE_MESSAGE_TOOLBAR_ADD_MENTION_KEY,
        ]}
        error={Boolean(touched && error)}
        medias={[]}
        placeholder={t('Feedback:RequestFeedback.RequestMessage.Placeholder')}
        setFieldValue={setFieldValue}
        textUseCase={TextUseCaseEnum.UserFeedbackRequest}
        topic={value}
        value={value}
        onChange={onChange}
        onTopicError={() => {
          setFieldTouched('requestMessage', true);
          setFieldError('requestMessage', 'Write a word or two for AI to work its magic!');
        }}
      />
    ),
  },
  skillIds: {
    name: 'skillIds',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      const requestedFromUserIds = useFormFieldValue('requestedFromUserIds');

      return (
        <RelatedSkills
          requestedFromUserIds={requestedFromUserIds}
          skillIds={value}
          onChange={onChange}
        />
      );
    },
  },
  mindsetIds: {
    name: 'mindsetIds',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      const requestedFromUserIds = useFormFieldValue('requestedFromUserIds');

      return (
        <RelatedSkills
          isMindset
          requestedFromUserIds={requestedFromUserIds}
          skillIds={value}
          onChange={onChange}
        />
      );
    },
  },
});

export const getSectionLayout = ({ t }: { t: TFunction }) => [
  {
    id: 'GeneralDetails',
    layout: ['requestedFromUserIds', 'requestMessage'],
  },
  {
    id: 'SkillRatings',
    layout: ['skillIds'],
    name: t('Feedback:CreateFeedback.Skills.Title'),
  },
  {
    id: 'MindsetRatings',
    layout: ['mindsetIds'],
    name: t('Feedback:CreateFeedback.Mindsets.Title'),
  },
];
